import { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthContext } from "../../../Context/AuthContext";
import { formatNgn } from "../../../Routes/Marketplace/libs/orderDisplayUtils";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";

const fetchEarnings = async (token) => {
  const response = await axios.get(
    `${process.env.REACT_APP_SERVER_URL}/order/orders/merchant/me/earnings`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

const PAYOUT_REQUEST_LABELS = {
  pending: { label: "Pending review", variant: "warning" },
  processing: { label: "Processing", variant: "warning" },
  paid: { label: "Paid", variant: "success" },
  rejected: { label: "Rejected", variant: "danger" },
};

const LINE_STATUS_VARIANT = {
  pending: "warning",
  available: "success",
  requested: "warning",
  paid: "success",
};

const MerchantPaymentPanel = ({ merchant }) => {
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [lineFilter, setLineFilter] = useState("all");
  const [expandedPayoutId, setExpandedPayoutId] = useState(null);

  const { data, isLoading, isError } = useQuery(
    ["merchantEarnings"],
    () => fetchEarnings(token),
    { enabled: !!token }
  );

  const payoutMutation = useMutation(
    async () => {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/order/orders/merchant/me/payout-request`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: (payload) => {
        toast.success(payload.message || "Payout request submitted");
        queryClient.invalidateQueries("merchantEarnings");
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Could not request payout");
      },
    }
  );

  const summary = data?.summary;
  const payouts = data?.payouts || [];
  const payoutReady = merchant?.payoutSetupComplete;

  const orderLines = useMemo(() => data?.summary?.orderLines ?? [], [data]);

  const filteredLines = useMemo(() => {
    if (lineFilter === "all") return orderLines;
    return orderLines.filter((line) => line.payoutStatus === lineFilter);
  }, [orderLines, lineFilter]);

  const lineColumns = [
    {
      key: "order",
      label: "Order",
      render: (row) => (
        <Link to="/merchant/orders" className="dashboard-link">
          {row.orderNumber ? `#${row.orderNumber}` : "View orders"}
        </Link>
      ),
    },
    { key: "productName", label: "Product" },
    { key: "quantity", label: "Qty" },
    {
      key: "amount",
      label: "Earnings",
      render: (row) => formatNgn(row.amount),
    },
    {
      key: "itemStatus",
      label: "Delivery",
      render: (row) => <StatusBadge status={row.itemStatus} />,
    },
    {
      key: "payoutStatus",
      label: "Payout",
      render: (row) => (
        <StatusBadge
          status={row.payoutStatusLabel || row.payoutStatus}
          variant={LINE_STATUS_VARIANT[row.payoutStatus] || "default"}
        />
      ),
    },
  ];

  return (
    <div className="dashboard-payments-panel">
      {!payoutReady && (
        <div className="dashboard-banner dashboard-banner--warning">
          Set up your payout method above before you can cash out delivered orders.
        </div>
      )}

      <div className="dashboard-earnings-grid">
        <div className="dashboard-earnings-card dashboard-earnings-card--primary">
          <span className="dashboard-earnings-card__label">Available to cash out</span>
          <strong className="dashboard-earnings-card__value">
            {isLoading ? "…" : formatNgn(summary?.available || 0)}
          </strong>
          <p className="dashboard-earnings-card__hint">From delivered & paid order lines</p>
        </div>
        <div className="dashboard-earnings-card">
          <span className="dashboard-earnings-card__label">Pending delivery</span>
          <strong className="dashboard-earnings-card__value">
            {isLoading ? "…" : formatNgn(summary?.pending || 0)}
          </strong>
        </div>
        <div className="dashboard-earnings-card">
          <span className="dashboard-earnings-card__label">In payout request</span>
          <strong className="dashboard-earnings-card__value">
            {isLoading ? "…" : formatNgn(summary?.requested || 0)}
          </strong>
        </div>
        <div className="dashboard-earnings-card">
          <span className="dashboard-earnings-card__label">Paid out</span>
          <strong className="dashboard-earnings-card__value">
            {isLoading ? "…" : formatNgn(summary?.paid || 0)}
          </strong>
        </div>
      </div>

      <div className="dashboard-form-actions" style={{ justifyContent: "flex-start", marginTop: 16 }}>
        <button
          type="button"
          className="dashboard-btn dashboard-btn--primary"
          disabled={
            !payoutReady ||
            payoutMutation.isLoading ||
            isLoading ||
            !summary?.available ||
            summary.available <= 0
          }
          onClick={() => payoutMutation.mutate()}
        >
          {payoutMutation.isLoading ? "Submitting…" : "Request payout"}
        </button>
      </div>

      <div className="dashboard-card" style={{ marginTop: 24 }}>
        <h3 className="dashboard-card__title">Earnings by order</h3>
        <p className="dashboard-form-hint">Each row is one order line linked to your payout status.</p>
        <div className="dashboard-filter-pills" style={{ marginBottom: 16 }}>
          {[
            ["all", "All"],
            ["pending", "Pending"],
            ["available", "Ready"],
            ["requested", "Requested"],
            ["paid", "Paid"],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={lineFilter === id ? "active" : ""}
              onClick={() => setLineFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        {isError ? (
          <p style={{ color: "var(--dash-danger)" }}>Could not load order earnings.</p>
        ) : (
          <DataTable
            columns={lineColumns}
            rows={filteredLines.map((line) => ({ ...line, _key: `${line.orderId}-${line.itemId}` }))}
            loading={isLoading}
            emptyMessage="No paid order lines yet."
          />
        )}
      </div>

      <div className="dashboard-card" style={{ marginTop: 24 }}>
        <h3 className="dashboard-card__title">Payout requests & linked orders</h3>
        {isError && <p style={{ color: "var(--dash-danger)" }}>Could not load payout history.</p>}
        {!isLoading && payouts.length === 0 && (
          <p style={{ color: "var(--dash-muted)", margin: 0 }}>No payout requests yet.</p>
        )}
        <div className="dashboard-payout-list">
          {payouts.map((payout) => {
            const status = PAYOUT_REQUEST_LABELS[payout.status] || PAYOUT_REQUEST_LABELS.pending;
            const expanded = expandedPayoutId === payout._id;
            return (
              <div key={payout._id} className="dashboard-payout-block">
                <button
                  type="button"
                  className="dashboard-payout-row dashboard-payout-row--button"
                  onClick={() => setExpandedPayoutId(expanded ? null : payout._id)}
                >
                  <div>
                    <strong>{formatNgn(payout.amount)}</strong>
                    <span className="dashboard-payout-row__count">
                      {payout.orderCount || payout.orderItemRefs?.length || 0} order line(s)
                    </span>
                    <p className="dashboard-payout-row__meta">
                      {new Date(payout.createdAt).toLocaleString()} · {payout.payoutDestination || payout.payoutMethod}
                    </p>
                  </div>
                  <StatusBadge status={status.label} variant={status.variant} />
                </button>
                {expanded && payout.orderItemRefs?.length > 0 && (
                  <ul className="dashboard-payout-order-list">
                    {payout.orderItemRefs.map((ref) => (
                      <li key={`${payout._id}-${ref.itemId}`}>
                        <span>{ref.productName}</span>
                        <span>{ref.orderNumber ? `#${ref.orderNumber}` : ""}</span>
                        <span>Qty {ref.quantity}</span>
                        <strong>{formatNgn(ref.amount)}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MerchantPaymentPanel;
